import type { CornerstoneToolsApi } from './globals';
import type { CornerstoneToolsApiBeta } from './tools/cstypes';
import { localizedTools } from './tools/localized';
import { installProximity } from './tools/proximity';
import { createSegmentTool, SEGMENT_TOOLS } from './tools/segments';
import { ARROW_CONFIGURATION, createArrowTool, createSpineLabelTool, createTextTool, startSpineLabelling } from './tools/text';

export const MOUSE = { left: 1, right: 2, middle: 4 } as const;

/**
 * Primary (left-button) tool ids: the IMEXHS `_validTools` set (spec section 2), so toolbar,
 * context menu and keyboard speak the same names as the viewer being replaced.
 */
export const TOOL_IDS = [
  'window-level', 'stack', 'pan', 'zoom', 'lines', 'angles', 'cobbs', 'probe', 'erase', 'magnify',
  'triangulation', 'ellipticalRoi', 'rectangleRoi', 'freehandMouseTool', 'salvati', 'Bidirectional',
  'parallelDistance', 'cardiothoracicIndex', 'coxometry', 'cobbAngleMV', 'referenceLines',
  'arrowAnnotate', 'textMarker', 'text', 'rotate-free',
] as const;

export type ToolId = (typeof TOOL_IDS)[number];

/** Tool id → cornerstone-tools 3.0.0-beta class shipped in public/lib (custom tools register below). */
const BUILT_IN: Partial<Record<ToolId, { className: string; name: string }>> = {
  'window-level': { className: 'WwwcTool', name: 'Wwwc' },
  stack: { className: 'StackScrollTool', name: 'StackScroll' },
  pan: { className: 'PanTool', name: 'Pan' },
  zoom: { className: 'ZoomTool', name: 'Zoom' },
  erase: { className: 'EraserTool', name: 'Eraser' },
  magnify: { className: 'MagnifyTool', name: 'Magnify' },
  'rotate-free': { className: 'RotateTool', name: 'Rotate' },
};

/** Tool id → beta measurement tool re-labelled for IMEXHS (see tools/localized.ts). */
const LOCALIZED: Partial<Record<ToolId, string>> = {
  lines: 'Length',
  angles: 'Angle',
  cobbs: 'CobbAngle',
  probe: 'Probe',
  ellipticalRoi: 'EllipticalRoi',
  rectangleRoi: 'RectangleRoi',
  freehandMouseTool: 'FreehandMouse',
};

/** Measurement/annotation tools stay passive when not selected, so existing marks remain editable. */
const ANNOTATION_TOOL_IDS = new Set<ToolId>([
  'lines', 'angles', 'cobbs', 'probe', 'ellipticalRoi', 'rectangleRoi', 'freehandMouseTool', 'salvati',
  'Bidirectional', 'parallelDistance', 'cardiothoracicIndex', 'coxometry', 'cobbAngleMV', 'arrowAnnotate',
  'textMarker', 'text',
]);

/**
 * Tools IMEXHS does not make touch-active (spec section 8). The pinned cornerstone-tools beta has no
 * `isTouchActive`: a one-finger touch goes to the FIRST active tool whose `options.touchEnable` is not
 * false (undefined counts as on). So every active tool states `touchEnable`: on for the left-button
 * tool (unless listed here), off for the tools bound to the middle / right button and the wheel.
 */
const NOT_TOUCH_ACTIVE = new Set<ToolId>(['Bidirectional', 'parallelDistance', 'coxometry', 'cardiothoracicIndex', 'cobbAngleMV']);

const ALWAYS_ON = [
  { className: 'StackScrollMouseWheelTool', name: 'StackScrollMouseWheel' },
  { className: 'ZoomTouchPinchTool', name: 'ZoomTouchPinch' },
];

/** Zoom limits of the IMEXHS zoom tool. */
const ZOOM_CONFIGURATION = { minScale: 0.1, maxScale: 20, preventZoomOutsideImage: false, invert: false };
const MAGNIFY_SIZE = 300;

type ToolMode = 'active' | 'passive' | 'enabled' | 'disabled';

/** A tool class added to every viewport element, with the configuration each instance gets. */
interface ToolEntry {
  toolClass: unknown;
  name: string;
  configuration?: Record<string, unknown>;
}

let api: CornerstoneToolsApiBeta | null = null;
let activeLeft: ToolId = 'window-level';
/** While true (a saved annotation version is shown) measurements are drawn but cannot be drawn or edited. */
let annotationsLocked = false;
const registered = new Map<ToolId, string>();
const listeners = new Set<(id: ToolId) => void>();
const entries: ToolEntry[] = [];
/** Last mode set per tool name, replayed on viewports created later. */
const modes = new Map<string, { mode: ToolMode; options?: Record<string, unknown> }>();

function toolClass(name: string): unknown {
  const cls = window.cornerstoneTools[name];
  if (!cls) throw new Error(`cornerstone-tools has no ${name}`);
  return cls;
}

/**
 * Registers every built-in and IMEXHS measurement tool once.
 *
 * The pinned 3.0.0 beta only adds a tool to elements that already exist and builds each instance
 * without props, so tools are attached here to every viewport element as cornerstone enables it
 * (with its configuration), and the current tool modes are replayed on it.
 */
export function initTools(magnifyFactor: number): CornerstoneToolsApi {
  if (api) return api;
  api = window.cornerstoneTools.init({ showSVGCursors: false }) as CornerstoneToolsApiBeta;
  installProximity(window.VIEWER_CONFIG as unknown as Record<string, unknown>, api.store);
  const { cornerstone } = window;
  cornerstone.events.addEventListener(cornerstone.EVENTS.ELEMENT_ENABLED, (event) => {
    attachElement((event as CustomEvent<{ element: HTMLElement }>).detail.element);
  });
  cornerstone.events.addEventListener(cornerstone.EVENTS.ELEMENT_DISABLED, (event) => {
    detachElement((event as CustomEvent<{ element: HTMLElement }>).detail.element);
  });

  const configurations: Partial<Record<ToolId, Record<string, unknown>>> = {
    magnify: { magnificationLevel: magnifyFactor, magnifySize: MAGNIFY_SIZE },
    zoom: ZOOM_CONFIGURATION,
  };
  for (const [id, def] of Object.entries(BUILT_IN) as Array<[ToolId, { className: string; name: string }]>) {
    addEntry({ toolClass: toolClass(def.className), name: def.name, configuration: configurations[id] });
    registered.set(id, def.name);
  }
  const localized = localizedTools();
  for (const [id, name] of Object.entries(LOCALIZED) as Array<[ToolId, string]>) {
    addEntry({ toolClass: localized[name], name });
    registered.set(id, name);
  }
  for (const def of ALWAYS_ON) addEntry({ toolClass: toolClass(def.className), name: def.name });
  registerMeasurementTools();
  return api;
}

/** The IMEXHS "Medidas" tools implemented in src/tools/. */
function registerMeasurementTools(): void {
  for (const [id, spec] of Object.entries(SEGMENT_TOOLS) as Array<[ToolId, (typeof SEGMENT_TOOLS)[keyof typeof SEGMENT_TOOLS]]>) {
    registerCustomTool(id, createSegmentTool(spec), spec.name);
  }
  registerCustomTool('arrowAnnotate', createArrowTool(), 'ArrowAnnotate', { configuration: ARROW_CONFIGURATION });
  registerCustomTool('text', createTextTool(), 'TextAnnotation');
  registerCustomTool('textMarker', createSpineLabelTool(), 'TextMarker');
}

function addEntry(entry: ToolEntry): void {
  entries.push(entry);
  for (const element of requireApi().store.state.enabledElements) addToElement(element, entry);
}

function addToElement(element: HTMLElement, entry: ToolEntry): void {
  const tools = requireApi();
  if (tools.getToolForElement(element, entry.name)) return;
  tools.addToolForElement(element, entry.toolClass);
  const instance = tools.getToolForElement(element, entry.name);
  if (instance && entry.configuration) instance.configuration = { ...instance.configuration, ...entry.configuration };
}

function attachElement(element: HTMLElement): void {
  const tools = requireApi();
  if (!tools.store.state.enabledElements.includes(element)) tools.addEnabledElement(element);
  for (const entry of entries) addToElement(element, entry);
  for (const [name, { mode, options }] of modes) applyModeForElement(element, name, mode, options);
}

/** Drops the tool instances of a destroyed viewport so the store does not keep dead elements. */
function detachElement(element: HTMLElement): void {
  const { state } = requireApi().store;
  state.tools = state.tools.filter((tool) => tool.element !== element);
  state.enabledElements = state.enabledElements.filter((el) => el !== element);
}

function applyModeForElement(element: HTMLElement, name: string, mode: ToolMode, options?: Record<string, unknown>): void {
  const tools = requireApi();
  if (!tools.getToolForElement(element, name)) return;
  if (mode === 'active') tools.setToolActiveForElement(element, name, options ?? {});
  else if (mode === 'passive') tools.setToolPassiveForElement(element, name, options);
  else if (mode === 'enabled') tools.setToolEnabledForElement(element, name, options);
  else tools.setToolDisabledForElement(element, name, options);
}

/** Sets a tool's mode on every viewport and remembers it for viewports created later. */
function setMode(name: string, mode: ToolMode, options?: Record<string, unknown>): void {
  modes.set(name, { mode, options });
  for (const element of requireApi().store.state.enabledElements) applyModeForElement(element, name, mode, options);
}

/**
 * Registers a tool implemented in this app (custom measurements, triangulation) under an IMEXHS id.
 * `name` is the cornerstone-tools tool name the class instance reports; `props.configuration`
 * is merged into every instance's configuration.
 */
export function registerCustomTool(id: ToolId, toolClassRef: unknown, name: string, props: Record<string, unknown> = {}): void {
  requireApi();
  const configuration = props.configuration as Record<string, unknown> | undefined;
  addEntry({ toolClass: toolClassRef, name, configuration });
  registered.set(id, name);
  if (ANNOTATION_TOOL_IDS.has(id)) setMode(name, 'passive');
}

export function isToolAvailable(id: ToolId): boolean {
  return registered.has(id);
}

function hasTouch(): boolean {
  return navigator.maxTouchPoints > 0;
}

/** Pinch zoom stays on, except on a touch screen while a measurement tool holds the finger. */
export function pinchAllowedWith(id: ToolId, touchScreen: boolean): boolean {
  return !(touchScreen && ANNOTATION_TOOL_IDS.has(id));
}

/** Default bindings (spec section 8): left = W/L, middle = pan, right = zoom, wheel = scroll, pinch = zoom. */
export function applyDefaultBindings(): void {
  setMode('Pan', 'active', { mouseButtonMask: MOUSE.middle, touchEnable: false });
  setMode('Zoom', 'active', { mouseButtonMask: MOUSE.right, touchEnable: false });
  setMode('StackScrollMouseWheel', 'active', { touchEnable: false });
  setMode('ZoomTouchPinch', 'active', { touchEnable: true });
  for (const id of ANNOTATION_TOOL_IDS) {
    const name = registered.get(id);
    if (name) setMode(name, 'passive');
  }
  setLeftTool('window-level');
}

/**
 * Puts `id` on the left mouse button; the previous left tool goes back to passive or disabled.
 * "Marcas Columna" first asks for the start label; cancelling keeps the previous tool.
 */
export function setLeftTool(id: ToolId): boolean {
  const name = registered.get(id);
  if (!name) return false;
  if (annotationsLocked && ANNOTATION_TOOL_IDS.has(id)) return false;
  if (id === 'textMarker') {
    void startSpineLabelling().then((started) => {
      if (started) activateLeft(id, name);
    });
    return true;
  }
  activateLeft(id, name);
  return true;
}

function activateLeft(id: ToolId, name: string): void {
  const previousName = registered.get(activeLeft);
  if (previousName && activeLeft !== id) {
    if (ANNOTATION_TOOL_IDS.has(activeLeft)) setMode(previousName, annotationsLocked ? 'enabled' : 'passive');
    else if (activeLeft === 'pan') setMode('Pan', 'active', { mouseButtonMask: MOUSE.middle, touchEnable: false });
    else if (activeLeft === 'zoom') setMode('Zoom', 'active', { mouseButtonMask: MOUSE.right, touchEnable: false });
    else setMode(previousName, 'disabled');
  }
  setMode(name, 'active', { mouseButtonMask: MOUSE.left, touchEnable: !NOT_TOUCH_ACTIVE.has(id) });
  // A second finger landing while a measurement is drawn by touch zoomed the image under it.
  setMode('ZoomTouchPinch', pinchAllowedWith(id, hasTouch()) ? 'active' : 'disabled', { touchEnable: true });
  activeLeft = id;
  listeners.forEach((listener) => listener(id));
}

/**
 * Read-only annotations (a saved version on screen): every measurement tool only draws ('enabled' mode),
 * the left button falls back to window/level if it held a measurement tool, and measurement tools cannot
 * be selected until this is turned off again.
 */
export function setAnnotationsReadOnly(readOnly: boolean): void {
  annotationsLocked = readOnly;
  if (readOnly && ANNOTATION_TOOL_IDS.has(activeLeft)) setLeftTool('window-level');
  for (const id of ANNOTATION_TOOL_IDS) {
    const name = registered.get(id);
    if (name) setMode(name, readOnly ? 'enabled' : 'passive');
  }
}

export function annotationsReadOnly(): boolean {
  return annotationsLocked;
}

/** Last mode set for a cornerstone-tools tool name (e2e checks; e.g. ZoomTouchPinch while measuring). */
export function toolMode(name: string): ToolMode | undefined {
  return modes.get(name)?.mode;
}

export function currentLeftTool(): ToolId {
  return activeLeft;
}

/** A measurement / annotation tool: a held finger there is placing a handle, not asking for a menu. */
export function isAnnotationTool(id: ToolId): boolean {
  return ANNOTATION_TOOL_IDS.has(id);
}

/** Called whenever the left tool changes (toolbar highlights, context menu state). */
export function onLeftToolChange(listener: (id: ToolId) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function requireApi(): CornerstoneToolsApiBeta {
  if (!api) throw new Error('initTools() must run first');
  return api;
}
